import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

const NotFound = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <SEO 
        title={t('notFound.seoTitle')}
        description={t('notFound.seoDescription')}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl w-full text-center"
        >
          {/* 404 Number */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <h1 className="text-[150px] md:text-[200px] font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 leading-none">
              404
            </h1>
          </motion.div>

          {/* Error Icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="w-24 h-24 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <svg 
              className="w-12 h-12 text-red-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </motion.div>

          {/* Error Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              {t('notFound.title')}
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-md mx-auto">
              {t('notFound.description')}
            </p>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/"
              className="px-8 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white font-semibold rounded-lg hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {t('notFound.goHome')}
            </Link>
            <button
              onClick={() => window.history.back()}
              className="px-8 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-all duration-300"
            >
              {t('notFound.goBack')}
            </button>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1 }}
            className="mt-12 pt-8 border-t border-gray-700"
          >
            <p className="text-gray-500 text-sm mb-4">{t('notFound.quickLinks')}</p>
            <div className="flex flex-wrap gap-4 justify-center text-sm">
              <Link to="/tuning" className="text-gray-400 hover:text-red-500 transition-colors">
                {t('notFound.linkTuning')}
              </Link>
              <Link to="/shop" className="text-gray-400 hover:text-red-500 transition-colors">
                {t('notFound.linkShop')}
              </Link>
              <Link to="/workshop" className="text-gray-400 hover:text-red-500 transition-colors">
                {t('notFound.linkWorkshop')}
              </Link>
              <Link to="/contact" className="text-gray-400 hover:text-red-500 transition-colors">
                {t('notFound.linkContact')}
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
};

export default NotFound;
